import HTTP from "@/_helpers/http";
import { AUTHENTICATION__API } from "@/_helpers/urls";
import toast from "@/services/Notification";

const getDefaultRegisterInfo = () => ({
  fname: "",
  lname: "",
  company_name: "",
  country: "",
  owner_type: 1,
  email: "",
  inv_referred_by: "",
  multiply_by: 0,
  password: "",
  confirmPassword: "",
  otp: "",
  referred_by: null,
  referred_by_manager: null,
  parent: false,
  otpSent: 0,
  otpPhoneNo: "",
});

const parseParentStatus = (value) =>
  value === true || String(value).toLowerCase() === "true";

const getParentStatusFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return parseParentStatus(urlParams.get("parent"));
};

const getParentStatus = (state) =>
  parseParentStatus(state.registerInfo.parent) || getParentStatusFromUrl();

const state = {
  registerInfo: getDefaultRegisterInfo(),
  resendTimer: 30,
  canResendOtp: false,
  resendInterval: null,
  resendAttempts: 0,
  maxResendAttempts: 3,
  token: null,
  isOtpSent: false,
  isOtpVerified: false,
  isRegistered: false,
  registrationError: null,
  otp_via: null,
};

const mutations = {
  REGISTER_ACCOUNT_TYPE(state, type) {
    if (type === "Individual") {
      state.registerInfo.owner_type = 1;
      state.registerInfo.otpSent = 0;
      state.registerInfo.company_name = "";
    } else if (type === "Company") {
      state.registerInfo.owner_type = 2;
      state.registerInfo.otpSent = 0;
      state.registerInfo.lname = "";
    }
  },

  SET_REGISTER_INFO(state, { field, value }) {
    state.registerInfo[field] = value;
  },

  SET_OTP_SENT(state, value) {
    state.isOtpSent = value;
  },

  SET_OTP_SENT_STEP(state, value) {
    state.registerInfo.otpSent = value;
  },

  SET_OTP_VERIFIED(state, value) {
    state.isOtpVerified = value;
  },

  SET_REGISTERED(state, value) {
    state.isRegistered = value;
  },

  SET_REGISTRATION_ERROR(state, error) {
    state.registrationError = error;
  },

  SET_TOKEN(state, data) {
    state.token = data;
  },

  SET_RESEND_TIMER(state, value) {
    state.resendTimer = value;
  },

  SET_CAN_RESEND(state, value) {
    state.canResendOtp = value;
  },

  CLEAR_RESEND_INTERVAL(state) {
    if (state.resendInterval) {
      clearInterval(state.resendInterval);
      state.resendInterval = null;
    }
  },

  SET_RESEND_INTERVAL(state, interval) {
    state.resendInterval = interval;
  },

  SET_RESEND_ATTEMPTS(state, value) {
    state.resendAttempts = value;
  },

  RESET(state) {
    state.registerInfo = getDefaultRegisterInfo();
    state.resendTimer = 30;
    state.canResendOtp = false;
    state.resendAttempts = 0;
    state.token = null;
    state.isOtpSent = false;
    state.isOtpVerified = false;
    state.isRegistered = false;
    state.registrationError = null;

    if (state.resendInterval) {
      clearInterval(state.resendInterval);
      state.resendInterval = null;
    }
  },

  SET_REFERRED_BY(state, value) {
    state.registerInfo.referred_by = value;
  },

  SET_MANAGER_REF_ID(state, value) {
    state.registerInfo.referred_by_manager = value;
  },

  SET_PARENT_STATUS(state, value) {
    state.registerInfo.parent = value;
  },

  OTP_VIA(state, value) {
    state.otp_via = value;
  },
};

const actions = {
  startResendTimer({ state, commit }, seconds = 30) {
    commit("SET_RESEND_TIMER", seconds);
    commit("SET_CAN_RESEND", false);
    commit("CLEAR_RESEND_INTERVAL");

    const interval = setInterval(() => {
      if (state.resendTimer > 0) {
        commit("SET_RESEND_TIMER", state.resendTimer - 1);
      } else {
        commit("SET_CAN_RESEND", true);
        commit("CLEAR_RESEND_INTERVAL");
      }
    }, 1000);

    commit("SET_RESEND_INTERVAL", interval);
  },

  initializeReferral({ commit }) {
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get("referred_by");
    const managerRefId = urlParams.get("referred_by_manager");
    const parentStatus = urlParams.get("parent");

    if (refId) {
      commit("SET_REFERRED_BY", refId);
    }

    if (managerRefId) {
      commit("SET_MANAGER_REF_ID", managerRefId);
    }

    commit("SET_PARENT_STATUS", parseParentStatus(parentStatus));
  },

  async isSmsOtp({ commit }) {
      try {
          const response = await HTTP({
              trackProgress: true,
              loadingText: 'Processing...'
          }).get(AUTHENTICATION__API.OTPVIA);
        
          commit("OTP_VIA", response?.data?.otp_via);
      } catch (error) {
          console.log(error);
      }
    },

  async sendOTP({ state, commit, dispatch }, payload = {}) {
    try {
      const isResend = payload.isResend === true;
      if (isResend && state.resendAttempts >= state.maxResendAttempts) {
        toast.error("Maximum resend attempts reached");
        return null;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const inv_referred_by = payload.inv_referred_by || urlParams.get("inv_referred_by") || null;
      const referred_by = payload.referred_by || urlParams.get("referred_by") || null;
      const referred_by_manager = payload.referred_by_manager || urlParams.get("referred_by_manager") || null;
      const parent_status =
        payload.parent !== undefined
          ? parseParentStatus(payload.parent)
          : getParentStatus(state);

      const fullName =
        state.registerInfo.owner_type === 2
          ? state.registerInfo.company_name
          : `${state.registerInfo.fname} ${state.registerInfo.lname}`.trim();

      const response = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.SENDREGISTEROTP, {
        name: fullName,
        country: state.registerInfo.country,
        email: state.registerInfo.email,
        inv_referred_by,
        multiply_by: state.registerInfo.multiply_by || null,
        password: state.registerInfo.password,
        owner_type: state.registerInfo.owner_type,
        otp: state.registerInfo.otp || null,
        referred_by,
        referred_by_manager,
        parent: parent_status,
        parent_status,
        otp_phone_no: state.registerInfo.otpPhoneNo || null,
        otp_via: payload.otp_via || "emailOtp",
      });

      commit("SET_OTP_SENT", true);
      commit("SET_TOKEN", response?.data?.token || null);
      commit("SET_OTP_SENT_STEP", 1);

      const nextResendAttempts = isResend ? state.resendAttempts + 1 : 0;
      commit("SET_RESEND_ATTEMPTS", nextResendAttempts);

      if (nextResendAttempts < state.maxResendAttempts) {
        dispatch("startResendTimer", 30);
      } else {
        commit("CLEAR_RESEND_INTERVAL");
        commit("SET_RESEND_TIMER", 0);
        commit("SET_CAN_RESEND", false);
      }

      toast.success(response?.data?.message);
      return response;
    } catch (error) {
      console.log(error?.response?.data?.message);
      commit("SET_OTP_SENT", false);
      commit("SET_OTP_SENT_STEP", 0);
      commit(
        "SET_REGISTRATION_ERROR",
        error?.response?.data?.message || "Failed to send OTP"
      );
      toast.error(error?.response?.data?.message || "Failed to send OTP");
      throw error;
    }
  },

  previousStep({ commit }) {
    commit("SET_OTP_SENT_STEP", 0);
  },

  async verifyOTP1({ state, commit }) {
    try {
      const response = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.AUTHACCOUNTVERIFY, {
        code: state.registerInfo.otp,
        token: state.token,
      });

      if (response.status === 200) {
        commit("SET_OTP_VERIFIED", true);
        return response;
      }

      commit("SET_OTP_VERIFIED", false);
      return null;
    } catch (error) {
      commit("SET_OTP_VERIFIED", false);
      commit("SET_REGISTRATION_ERROR", "OTP verification failed");
      toast.error(error?.response?.data?.message || "OTP verification failed.");
      throw error;
    }
  },

  async verifyOTP({ state, commit }) {
    try {
      const response = await HTTP({
        trackProgress: true,
        loadingText: 'Processing...'
      }).post(
        AUTHENTICATION__API.AUTHACCOUNTVERIFY,
        {
          code: state.registerInfo.otp,
          token: state.token,
          email: state.registerInfo.email 
        }
      );
      if (response.status === 200) {
        commit("SET_OTP_VERIFIED", true);
        return;
      }
    } catch (error) {
    commit("SET_OTP_VERIFIED", false);
    const message = error.response?.data?.message || "OTP verification failed";
    commit("SET_REGISTRATION_ERROR", message);
    toast.error(message);
    }
  },

  async register({ state, commit }) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const referred_by = urlParams.get("referred_by") || null;
      const inv_referred_by = urlParams.get("inv_referred_by") || null;
      const referred_by_manager = urlParams.get("referred_by_manager") || null;
      const parent_status = getParentStatus(state);

      const res = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.AUTHREGISTER, {
        fname:
          state.registerInfo.owner_type === 2
            ? state.registerInfo.company_name
            : state.registerInfo.fname,
        lname: state.registerInfo.owner_type === 2 ? "" : state.registerInfo.lname,
        company_name:
          state.registerInfo.owner_type === 2
            ? state.registerInfo.company_name
            : "",
        country: state.registerInfo.country,
        email: state.registerInfo.email,
        inv_referred_by,
        multiply_by: state.registerInfo.multiply_by || null,
        password: state.registerInfo.password,
        owner_type: state.registerInfo.owner_type,
        otp: state.registerInfo.otp,
        referred_by,
        referred_by_manager,
        parent: parent_status,
        parent_status,
        otp_phone_no: state.registerInfo.otpPhoneNo || null,
      });

      commit("SET_REGISTERED", true);

      if (res.status === 200) {
        toast.success("Registration success");
        commit("RESET");
      }

      return res;
    } catch (error) {
      console.log(error);
      commit(
        "SET_REGISTRATION_ERROR",
        error?.response?.data?.message || "Registration failed!"
      );
      toast.error(error?.response?.data?.message || "Registration failed!");
      throw error;
    }
  },
};

const getters = {
  getRegisterInfo: (state) => state.registerInfo,
  isOtpSent: (state) => state.isOtpSent,
  isOtpVerified: (state) => state.isOtpVerified,
  isRegistered: (state) => state.isRegistered,
  registrationError: (state) => state.registrationError,
  resendTimer: (state) => state.resendTimer,
  canResendOtp: (state) => state.canResendOtp,
  resendAttempts: (state) => state.resendAttempts,
  maxResendAttempts: (state) => state.maxResendAttempts,
  remainingResendAttempts: (state) =>
    Math.max(state.maxResendAttempts - state.resendAttempts, 0),
  canTryResendOtp: (state) =>
    state.canResendOtp && state.resendAttempts < state.maxResendAttempts,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
